<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/ui/AppIcon.vue'
import { useScrollHeader } from '@/composables/useScrollHeader'

/**
 * Шапка внутренней страницы: стрелка назад + заголовок 20 Bold.
 * Всегда прилипшая. Блок из слота `sub` (табы, кнопки) при скролле вниз
 * сворачивается под название, при скролле вверх — возвращается.
 */
interface Props {
  title: string
  /** Подзаголовок красным под названием — режим получения в оформлении заказа */
  subtitle?: string
  /** Назад обрабатывает родитель — например, шаги «Старта» без своих роутов */
  customBack?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  back: []
}>()

const router = useRouter()

const { t } = useI18n()

const { isHidden: isSubCollapsed, isStuck } = useScrollHeader(80)

/*
 * Высота sub-блока измеряется, чтобы при сворачивании компенсировать её
 * нижним отступом: суммарная высота шапки в потоке не меняется, документ
 * не «дышит» — иначе на коротких страницах клампится scrollY и появляются
 * ложные дельты направления (блок начинает прыгать при быстром скролле).
 */
const subInner = ref<HTMLElement | null>(null)
const subHeight = ref(0)
let observer: ResizeObserver | null = null

onMounted(() => {
  if (!subInner.value) return
  observer = new ResizeObserver(() => {
    subHeight.value = subInner.value?.scrollHeight ?? 0
  })
  observer.observe(subInner.value)
  subHeight.value = subInner.value.scrollHeight
})

onUnmounted(() => observer?.disconnect())

function goBack(): void {
  if (props.customBack) {
    emit('back')
    return
  }
  router.back()
}
</script>

<template>
  <header
    class="page-header"
    :class="{ 'page-header--stuck': isStuck, 'page-header--collapsed': isSubCollapsed }"
    :style="{ '--sub-height': `${subHeight}px` }"
  >
    <div class="page-header__bar">
      <button type="button" class="page-header__back" :aria-label="t('common.back')" @click="goBack">
        <AppIcon name="arrow-left" :size="24" />
      </button>
      <div class="page-header__heading" :class="{ 'page-header__heading--dense': subtitle }">
        <h1 class="page-header__title">{{ title }}</h1>
        <p v-if="subtitle" class="page-header__subtitle font-13-b">{{ subtitle }}</p>
      </div>
      <div v-if="$slots.action" class="page-header__action">
        <slot name="action" />
      </div>
    </div>

    <div v-if="$slots.sub" class="page-header__sub">
      <div ref="subInner" class="page-header__sub-inner">
        <slot name="sub" />
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.page-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--surface-card);
  transition:
    box-shadow 0.25s,
    margin-bottom 0.25s;

  &--stuck {
    border-radius: 0 0 var(--radius-xl) var(--radius-xl);
    box-shadow: var(--shadow-soft);
  }

  /* Компенсация высоты свёрнутого sub — высота документа постоянна */
  &--collapsed {
    margin-bottom: var(--sub-height, 0px);
  }

  &__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 60px;
    padding: 0 var(--screen-padding);
  }

  &__back {
    display: flex;
    padding: 0;
    border: 0;
    background: none;
    color: var(--text-color);
    cursor: pointer;
  }

  &__heading {
    min-width: 0;

    /* Двухстрочный вариант из макета — название мельче, чтобы влезть в 60px */
    &--dense .page-header__title {
      font-size: 18px;
      line-height: 28px;
    }
  }

  &__title {
    font-size: 20px;
    font-weight: var(--fw-bold);
    line-height: 24px;
  }

  &__subtitle {
    line-height: 16px;
    color: var(--primary-500);
  }

  /* Действие справа: пилюля баллов, кнопка «Очистить» и т.п. */
  &__action {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  /*
   * Сворачивание блока неизвестной высоты: grid-строка 1fr -> 0fr.
   * Скролл вниз — блок уходит под название, вверх — выезжает обратно.
   */
  &__sub {
    display: grid;
    grid-template-rows: 1fr;
    transition:
      grid-template-rows 0.25s,
      opacity 0.25s;
  }

  &--collapsed &__sub {
    grid-template-rows: 0fr;
    opacity: 0;
    pointer-events: none;
  }

  /* Отступы здесь, а не в слоте: scrollHeight учитывает padding, не margin */
  &__sub-inner {
    min-height: 0;
    overflow: hidden;
    padding: 0 var(--screen-padding);

    &::after {
      content: '';
      display: block;
      height: var(--screen-padding);
    }
  }
}
</style>
